import React from 'react';

import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Spinner from 'react-bootstrap/Spinner';
import * as FileSaver from 'file-saver';
import { PDFDocument, degrees } from 'pdf-lib';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useComponentState from '../../utils/useComponentState';
import { PRINTER_TASKS, connectPrinterForTask } from '../../utils/printerSettings';
import { getCarrierBook, getGiftBoxPdf, getQrPdf } from '../../api/Vouchers';
import {
  generateAndSavePdf,
  formatAmount,
  dedupeLabels,
  escapeZplHex,
} from '../../screens/ViewVouchers/ViewVouchersUtils';
import {
  BOOK_VARIANTS,
  QR_METHODS,
  LABEL_FIELDS,
  MAX_LABEL_FIELDS,
  PRINT_OPTIONS_INITIAL_STATE,
} from '../../constants/viewVouchers';

import {
  faBook,
  faGift,
  faQrcode,
  faEnvelope,
  faBoxOpen,
  faPrint,
  faCheck,
  faPlus,
} from '@fortawesome/free-solid-svg-icons';

import styles from './PrintOptionsModal.module.scss';

const valueForLabelField = (
  key,
  { ownerName, sender, amount, id, purchaseId, invoiceNumber }
) => {
  switch (key) {
    case 'receiverName':
      return ownerName;
    case 'senderName':
      return sender;
    case 'amount':
      return `LKR ${formatAmount(amount)}`;
    case 'purchaseId':
      return purchaseId;
    case 'voucherId':
      return id;
    case 'voucherNumber':
      return invoiceNumber;
    default:
      return '';
  }
};

const computeLabelFieldValues = (fields, entry) => {
  let topField = '';
  let bottomField = null;

  if (fields.length > 1) {
    if (fields.includes('voucherId')) {
      const otherKey = fields.find((key) => key !== 'voucherId');
      topField = valueForLabelField(otherKey, entry) || '';
      bottomField = entry.id;
    } else {
      topField = valueForLabelField(fields[0], entry) || '';
      bottomField = valueForLabelField(fields[1], entry) || null;
    }
  } else {
    topField = valueForLabelField(fields[0], entry) || '';
  }

  return { topField, bottomField };
};

const buildLabelZpl = (labelsToPrint, fields, quantity = 1) => {
  let zpl = '';

  dedupeLabels(labelsToPrint).forEach((entry) => {
    const { topField, bottomField } = computeLabelFieldValues(fields, entry);

    const fontsize = topField.length <= 20 ? 45 : topField.length <= 30 ? 40 : 25;
    const topFieldYOffset = fields.length > 1 ? 40 : 50;
    const bottomFieldYOffset = fields.length > 1 ? 120 : 130;
    const footerYOffset = 180;

    const labelZpl = `^XA
^CI28
^MD22
^PW799
^LL300
^FO0,${topFieldYOffset}
^FB799,1,0,C,0
^CF0,${fontsize},${fontsize}
^FH^FD${escapeZplHex(topField)}^FS

${
      bottomField
        ? `^FO0,${bottomFieldYOffset}
^FB799,1,0,C,0
^A0N,25,25
^FH^FD${escapeZplHex(bottomField)}^FS`
        : ''
    }

^FO0,${footerYOffset}
^FB799,1,0,C,0
^A0N,18,18
^FH^FDOne voucher, 300+ merchants | thyaga.lk^FS
^XZ`;

    zpl += labelZpl.repeat(quantity);
  });

  return zpl;
};

const rotatePdfToPortrait = async (base64Pdf) => {
  const pdfBytes = Buffer.from(base64Pdf, 'base64');
  const pdfDoc = await PDFDocument.load(pdfBytes);

  pdfDoc.getPages().forEach((page) => {
    const { width, height } = page.getSize();
    if (width > height) {
      page.setRotation(degrees(90));
    }
  });

  return pdfDoc.save();
};

const PrintOptionsModal = (props) => {
  const { show, handleClose, voucherIds = [], labelsToPrint = [], token } = props;

  const [
    {
      bookEnabled,
      bookVariant,
      giftBoxEnabled,
      giftBoxVariant,
      qrEnabled,
      qrMethod,
      envelopeEnabled,
      envelopeFields,
      envelopeQuantity,
      packageEnabled,
      packageFields,
      packageQuantity,
      printing,
    },
    setState,
  ] = useComponentState(PRINT_OPTIONS_INITIAL_STATE);

  const handleModalClose = () => {
    setState(PRINT_OPTIONS_INITIAL_STATE);
    handleClose();
  };

  const toggleLabelField = (fieldsKey, key) => {
    setState((prev) => {
      const current = prev[fieldsKey];
      let updated;
      if (current.includes(key)) {
        updated = current.filter((item) => item !== key);
      } else if (current.length >= MAX_LABEL_FIELDS) {
        return prev;
      } else {
        updated = [...current, key];
      }
      return { ...prev, [fieldsKey]: updated };
    });
  };

  const handlePackageQuantityChange = (value) => {
    if (value === '' || /^[0-9]{1,3}$/.test(value)) {
      setState({ packageQuantity: value });
    }
  };

  const handlePackageQuantityBlur = () => {
    const num = Number(packageQuantity);
    const clamped = !num || num < 1 ? 1 : num > 999 ? 999 : num;
    setState({ packageQuantity: clamped });
  };

  const handleEnvelopeQuantityChange = (value) => {
    if (value === '' || /^[0-9]{1,3}$/.test(value)) {
      setState({ envelopeQuantity: value });
    }
  };

  const handleEnvelopeQuantityBlur = () => {
    const num = Number(envelopeQuantity);
    const clamped = !num || num < 1 ? 1 : num > 999 ? 999 : num;
    setState({ envelopeQuantity: clamped });
  };

  const printBook = async () => {
    const variant = BOOK_VARIANTS.find((item) => item.value === bookVariant);
    const request = voucherIds.map((id) => ({
      voucherId: id,
      amount: true,
      to: variant.to,
      from: variant.from,
      body: variant.body,
    }));
    const carrierBookResponse = await getCarrierBook(request, token);
    const rotatedPdfBytes = await rotatePdfToPortrait(carrierBookResponse);
    const blob = new Blob([rotatedPdfBytes], { type: 'application/pdf' });
    FileSaver.saveAs(blob, `Print_Book_${variant.value.replace(/ /g, '_')}`);
  };

  const printGiftBox = async () => {
    const variant = BOOK_VARIANTS.find((item) => item.value === giftBoxVariant);
    const request = voucherIds.map((id) => ({
      voucherId: id,
      amount: true,
      to: variant.to,
      from: variant.from,
      body: variant.body,
    }));
    const giftBoxResponse = await getGiftBoxPdf(request, token);
    const pdfBytes = Buffer.from(giftBoxResponse, 'base64');
    const blob = new Blob([pdfBytes], { type: 'application/pdf' });
    FileSaver.saveAs(blob, `Print_Gift_Box_${variant.value.replace(/ /g, '_')}`);
  };

  const printQrZebra = async () => {
    const SHEET_SIZE = 3;
    let index = 0;
    let device = null;

    try {
      device = await connectPrinterForTask(PRINTER_TASKS.voucherQr.key, token);
      const encoder = new TextEncoder();

      while (index < voucherIds.length) {
        let zpl = '^XA\n^MD22\n^PW799\n^LL799\n';

        for (let pos = 0; pos < SHEET_SIZE && index < voucherIds.length; pos++) {
          const id = voucherIds[index];
          const positionX = pos === 0 ? 38 : pos === 1 ? 302 : 566;
          const textX = pos === 0 ? 70 : pos === 1 ? 334 : 600;

          zpl += `^FO${positionX},6^BQN,2,6^FDQA,https://thyaga.lk/checkbalance/${id}^FS\n`;
          zpl += `^FO${textX},220^A0N,28,28^FD${id}^FS\n`;

          index++;
        }

        zpl += '^XZ\n';
        await device.transferOut(1, encoder.encode(zpl));
        await new Promise((resolve) => setTimeout(resolve, 800));
      }
    } finally {
      if (device) {
        try {
          await device.releaseInterface(0);
        } catch (e) {}
        try {
          await device.close();
        } catch (e) {}
      }
    }
  };

  const downloadQrPdf = async () => {
    const request = { voucherIds };
    const rawPdfResponse = await getQrPdf(request, token);
    generateAndSavePdf(rawPdfResponse, 'QR_Code');
  };

  const printLabelZebra = async (fields, taskKey, quantity = 1) => {
    const zpl = buildLabelZpl(labelsToPrint, fields, quantity);
    const device = await connectPrinterForTask(taskKey, token);
    const encoder = new TextEncoder();
    await device.transferOut(1, encoder.encode(zpl));
  };

  const handlePrintSelected = async () => {
    setState({ printing: true });
    const failedSections = [];

    if (bookEnabled) {
      try {
        await printBook();
      } catch (error) {
        console.error('Print options: failed to generate book', error);
        failedSections.push('Print book');
      }
    }

    if (giftBoxEnabled) {
      try {
        await printGiftBox();
      } catch (error) {
        console.error('Print options: failed to generate gift box', error);
        failedSections.push('Print gift box');
      }
    }

    if (qrEnabled) {
      try {
        if (qrMethod === 'zebra') {
          await printQrZebra();
        } else {
          await downloadQrPdf();
        }
      } catch (error) {
        console.error('Print options: failed to process QR codes', error);
        failedSections.push('QR codes');
      }
    }

    if (envelopeEnabled && envelopeFields.length > 0) {
      try {
        await printLabelZebra(
          envelopeFields,
          PRINTER_TASKS.voucherEnvelope.key,
          Number(envelopeQuantity) || 1
        );
      } catch (error) {
        console.error('Print options: failed to print envelope / box label', error);
        failedSections.push('Envelope / box label');
      }
    }

    if (packageEnabled && packageFields.length > 0) {
      try {
        await printLabelZebra(
          packageFields,
          PRINTER_TASKS.voucherPackage.key,
          Number(packageQuantity) || 1
        );
      } catch (error) {
        console.error('Print options: failed to print package label', error);
        failedSections.push('Package label');
      }
    }

    setState({ printing: false });

    if (failedSections.length === 0) {
      toast.success('Selected outputs processed successfully');
      handleModalClose();
    } else {
      toast.error(`Failed to process: ${failedSections.join(', ')}`);
    }
  };

  const outputsSelectedCount = [
    bookEnabled,
    giftBoxEnabled,
    qrEnabled,
    envelopeEnabled,
    packageEnabled,
  ].filter(
    Boolean
  ).length;

  const envelopeInvalid = envelopeEnabled && envelopeFields.length === 0;
  const packageInvalid = packageEnabled && packageFields.length === 0;
  const canPrint =
    outputsSelectedCount > 0 && !envelopeInvalid && !packageInvalid && !printing;

  const renderFieldChips = (fieldsKey, fields, sectionClass, enabled) => (
    <div className={styles.chipRow}>
      {LABEL_FIELDS.map(({ key, label }) => {
        const selected = fields.includes(key);
        const disabled = !enabled || (!selected && fields.length >= MAX_LABEL_FIELDS);
        return (
          <button
            type='button'
            key={key}
            className={`${styles.chip} ${sectionClass} ${selected ? styles.chipSelected : ''}`}
            disabled={disabled}
            onClick={() => toggleLabelField(fieldsKey, key)}
          >
            <FontAwesomeIcon icon={selected ? faCheck : faPlus} className={styles.chipIcon} />
            {label}
          </button>
        );
      })}
    </div>
  );

  return (
    <>
      {show && <div className={styles.customBackdrop} onClick={handleModalClose} />}
      <Modal
        show={show}
        onHide={handleModalClose}
        backdrop={false}
        centered
        size='lg'
        className={styles.modal}
      >
      <Modal.Header closeButton>
        <Modal.Title className={styles.titleRow}>
          <span>Print options</span>
          <span className={styles.subtitle}>
            {voucherIds.length} voucher{voucherIds.length !== 1 ? 's' : ''} selected
          </span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className={styles.section}>
          <div
            className={styles.sectionHeader}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ bookEnabled: !bookEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={bookEnabled}
              onChange={(e) => setState({ bookEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faBook} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print book</p>
              <p className={styles.sectionSubtitle}>Choose what shows on the voucher</p>
            </div>
            <Form.Control
              as='select'
              className={styles.sectionSelect}
              disabled={!bookEnabled}
              value={bookVariant}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setState({ bookVariant: e.target.value })}
            >
              {BOOK_VARIANTS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className={styles.section}>
          <div
            className={styles.sectionHeader}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ giftBoxEnabled: !giftBoxEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={giftBoxEnabled}
              onChange={(e) => setState({ giftBoxEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faGift} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print gift box</p>
              <p className={styles.sectionSubtitle}>Choose what shows on the gift box</p>
            </div>
            <Form.Control
              as='select'
              className={styles.sectionSelect}
              disabled={!giftBoxEnabled}
              value={giftBoxVariant}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setState({ giftBoxVariant: e.target.value })}
            >
              {BOOK_VARIANTS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className={styles.section}>
          <div
            className={styles.sectionHeader}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ qrEnabled: !qrEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={qrEnabled}
              onChange={(e) => setState({ qrEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faQrcode} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>QR codes</p>
              <p className={styles.sectionSubtitle}>Pick output method</p>
            </div>
            <Form.Control
              as='select'
              className={styles.sectionSelect}
              disabled={!qrEnabled}
              value={qrMethod}
              onClick={(e) => e.stopPropagation()}
              onChange={(e) => setState({ qrMethod: e.target.value })}
            >
              {QR_METHODS.map(({ value, label }) => (
                <option key={value} value={value}>
                  {label}
                </option>
              ))}
            </Form.Control>
          </div>
        </div>

        <div className={`${styles.section} ${styles.envelopeSection}`}>
          <div
            className={`${styles.sectionHeader} ${styles.envelopeHeader}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ envelopeEnabled: !envelopeEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={envelopeEnabled}
              onChange={(e) => setState({ envelopeEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faEnvelope} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print label on envelope / box</p>
            </div>
            <div className={styles.qtyGroup}>
              <span className={styles.qtyLabel}>How many copies</span>
              <Form.Control
                type='text'
                inputMode='numeric'
                maxLength={3}
                className={styles.qtyInput}
                disabled={!envelopeEnabled}
                value={envelopeQuantity}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handleEnvelopeQuantityChange(e.target.value)}
                onBlur={handleEnvelopeQuantityBlur}
              />
            </div>
          </div>
          {renderFieldChips('envelopeFields', envelopeFields, styles.envelopeChip, envelopeEnabled)}
          {envelopeInvalid && (
            <small className='text-danger'>Choose at least one field</small>
          )}
        </div>

        <div className={`${styles.section} ${styles.packageSection}`}>
          <div
            className={`${styles.sectionHeader} ${styles.packageHeader}`}
            style={{ cursor: 'pointer' }}
            onClick={() => setState({ packageEnabled: !packageEnabled })}
          >
            <Form.Check
              type='checkbox'
              checked={packageEnabled}
              onChange={(e) => setState({ packageEnabled: e.target.checked })}
              onClick={(e) => e.stopPropagation()}
            />
            <FontAwesomeIcon icon={faBoxOpen} className={styles.sectionIcon} />
            <div className={styles.sectionText}>
              <p className={styles.sectionTitle}>Print label on package</p>
            </div>
            <div className={styles.qtyGroup}>
              <span className={styles.qtyLabel}>How many copies</span>
              <Form.Control
                type='text'
                inputMode='numeric'
                maxLength={3}
                className={styles.qtyInput}
                disabled={!packageEnabled}
                value={packageQuantity}
                onClick={(e) => e.stopPropagation()}
                onChange={(e) => handlePackageQuantityChange(e.target.value)}
                onBlur={handlePackageQuantityBlur}
              />
            </div>
          </div>
          {renderFieldChips('packageFields', packageFields, styles.packageChip, packageEnabled)}
          {packageInvalid && (
            <small className='text-danger'>Choose at least one field</small>
          )}
        </div>
      </Modal.Body>
      <Modal.Footer className={styles.footer}>
        <span className={styles.outputsSelected}>
          {outputsSelectedCount} output{outputsSelectedCount !== 1 ? 's' : ''} selected
        </span>
        <div style={{ display: 'flex', gap: '10px' }}>
          <Button variant='secondary' onClick={handleModalClose}>
            Cancel
          </Button>
          <Button variant='primary' onClick={handlePrintSelected} disabled={!canPrint}>
            {printing ? (
              <Spinner size='sm' animation='border' />
            ) : (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <FontAwesomeIcon icon={faPrint} />
                Print selected
              </span>
            )}
          </Button>
        </div>
      </Modal.Footer>
      </Modal>
    </>
  );
};

export default PrintOptionsModal;
